/**
 * Clock Widget - Displays current time and optionally date
 */

import type { Widget, WidgetConfig } from './index';

export interface ClockConfig extends WidgetConfig {
  use24h?: boolean;
  showSeconds?: boolean;
  showDate?: boolean;
  timezone?: string;
  fontSize?: number;
  color?: string;
  fontFamily?: string;
  dateFormat?: 'short' | 'long' | 'full';
  widgetBg?: string;
  widgetBgTransparent?: boolean;
  // Editor compat aliases
  format?: string;
  location?: string;
}

export class ClockWidget implements Widget {
  private container: HTMLElement;
  private config: ClockConfig;
  private intervalId: ReturnType<typeof setInterval> | null = null;

  constructor(config: ClockConfig = {}) {
    // Map editor property names to player config
    const mapped: Record<string, unknown> = { ...config };
    if ('format' in mapped && !('use24h' in mapped)) {
      mapped.use24h = mapped.format === '24h';
    }
    if ('location' in mapped && !('city' in mapped)) {
      mapped.city = mapped.location;
    }

    this.config = {
      use24h: true,
      showSeconds: true,
      showDate: false,
      fontSize: 48,
      color: '#ffffff',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      dateFormat: 'long',
      ...mapped,
    };

    this.container = document.createElement('div');
    this.container.className = 'widget-clock';
  }

  render(): HTMLElement {
    const bg = this.config.widgetBgTransparent
      ? 'transparent'
      : (this.config.widgetBg || 'transparent');

    this.container.style.cssText = `
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100%;
      font-family: ${this.config.fontFamily};
      color: ${this.config.color};
      background: ${bg};
    `;

    this.updateDisplay();
    this.intervalId = setInterval(() => this.updateDisplay(), 1000);

    return this.container;
  }

  update(): void {
    this.updateDisplay();
  }

  private updateDisplay(): void {
    const now = this.config.timezone
      ? new Date(new Date().toLocaleString('en-US', { timeZone: this.config.timezone }))
      : new Date();

    let hours = now.getHours();
    const minutes = now.getMinutes().toString().padStart(2, '0');
    const seconds = now.getSeconds().toString().padStart(2, '0');
    let ampm = '';

    if (!this.config.use24h) {
      ampm = hours >= 12 ? ' PM' : ' AM';
      hours = hours % 12 || 12;
    }

    let timeStr = `${hours.toString().padStart(2, '0')}:${minutes}`;
    if (this.config.showSeconds) {
      timeStr += `:${seconds}`;
    }
    timeStr += ampm;

    let html = `
      <div style="font-size: ${this.config.fontSize}px; font-weight: bold; line-height: 1;">
        ${timeStr}
      </div>
    `;

    if (this.config.showDate) {
      const dateOptions: Intl.DateTimeFormatOptions = {
        weekday: this.config.dateFormat === 'short' ? 'short' : 'long',
        day: 'numeric',
        month: this.config.dateFormat === 'short' ? 'short' : 'long',
        year: this.config.dateFormat === 'full' ? 'numeric' : undefined,
      };

      if (this.config.timezone) {
        dateOptions.timeZone = this.config.timezone;
      }

      const dateStr = now.toLocaleDateString('tr-TR', dateOptions);
      html += `
        <div style="font-size: ${Math.round(this.config.fontSize! * 0.35)}px; opacity: 0.8; margin-top: 8px;">
          ${dateStr}
        </div>
      `;
    }

    this.container.innerHTML = html;
  }

  destroy(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }
}
